import { createRoute, Link } from "@tanstack/react-router";
import { useSession } from "@/frontend/auth-client";
import { Building2, User as UserIcon, Users, ShieldCheck, Key, Upload, Plug, Webhook, CreditCard, Lock, AppWindow } from "lucide-react";
import { cn } from "@/frontend/lib/utils";
import { Route as dashboardLayout } from "./_layout";
import { DestinationsSection } from "@/frontend/components/dashboard/settings/DestinationsSection";
import { useActiveMemberQuery } from "@/frontend/queries/dashboard/settings";
import { useBillingStateQuery } from "@/frontend/queries/dashboard/billing";
import { ProfileSection } from "@/frontend/components/dashboard/settings/ProfileSection";
import { OrganizationSection } from "@/frontend/components/dashboard/settings/OrganizationSection";
import { MembersSection } from "@/frontend/components/dashboard/settings/MembersSection";
import { UserManagementSection } from "@/frontend/components/dashboard/settings/UserManagementSection";
import { ApiKeysSection } from "@/frontend/components/dashboard/settings/ApiKeysSection";
import { WebhooksSection } from "@/frontend/components/dashboard/settings/WebhooksSection";
import { ClientsSection } from "@/frontend/components/dashboard/settings/ClientsSection";
import { UsageSection } from "@/frontend/components/dashboard/settings/UsageSection";
import { withFeatureGate } from "@/frontend/components/dashboard/settings/FeatureLocked";
import type { Feature } from "@/server/types/billing";
import { McpServerSection } from "@/frontend/components/dashboard/settings/ConnectedAppsSection";
import type {
  NavSection,
  SettingsSearch,
} from "@/frontend/types/dashboard/settings";

export const Route = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/dashboard/settings",
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({
    section: (search.section as string) || "profile",
    destinationId: (search.destinationId as string) || undefined,
    provider: (search.provider as string) || "",
    webhookId: (search.webhookId as string) || undefined,
  }),
  component: SettingsPage,
});

function isOrgAdminOrOwner(role: string | null | undefined) {
  if (!role) return false;
  return role
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
    .some((value) => value === "admin" || value === "owner");
}

const navSections: NavSection[] = [
  { id: "profile", label: "My Profile", icon: UserIcon, group: "Account" },
  { id: "organization", label: "General", icon: Building2, group: "Workspace" },
  { id: "members", label: "Members", icon: Users, group: "Workspace" },
  { id: "usage", label: "Usage", icon: CreditCard, group: "Workspace", adminOrOwnerOnly: true },
  { id: "clients", label: "Connections", icon: Plug, group: "Platform", adminOrOwnerOnly: true },
  { id: "destinations", label: "Export", icon: Upload, group: "Platform", adminOrOwnerOnly: true, requiresFeature: "export_destinations" },
  { id: "users", label: "User Management", icon: ShieldCheck, group: "Platform", platformAdminOnly: true },
  { id: "apiKeys", label: "API Keys", icon: Key, group: "Integrations" },
  { id: "webhooks", label: "Webhooks", icon: Webhook, group: "Integrations", adminOrOwnerOnly: true },
  { id: "mcpServer", label: "MCP server", icon: AppWindow, group: "Integrations" },
];

const sectionComponents: Record<string, React.ComponentType> = {
  profile: ProfileSection,
  organization: OrganizationSection,
  members: MembersSection,
  usage: UsageSection,
  users: UserManagementSection,
  destinations: withFeatureGate(DestinationsSection, "export_destinations", {
    title: "Export destinations",
    description: "Push FHIR resources to S3, GCS, Azure, or a downstream FHIR server on every pull completion.",
  }),
  apiKeys: ApiKeysSection,
  webhooks: WebhooksSection,
  mcpServer: McpServerSection,
};

/** Settings page — role-filtered sidebar with billing-based lock badges; gated sections render `FeatureLocked` in the main panel. */
function SettingsPage() {
  const { section, provider } = Route.useSearch();
  const { data: session } = useSession();
  const isPlatformAdmin = session?.user?.role === "admin";
  const activeMemberQuery = useActiveMemberQuery(!!session && !isPlatformAdmin);
  const canAccessExport = isPlatformAdmin || isOrgAdminOrOwner(activeMemberQuery.data?.role);
  const billingQuery = useBillingStateQuery();
  const features = billingQuery.data?.features ?? null;
  const isFeatureEnabled = (feat: Feature | undefined): boolean => {
    if (!feat) return true;
    // While billing loads, treat features as enabled to avoid flashing a lock icon.
    if (!features) return true;
    return features[feat] === true;
  };

  // Show every entry the caller has role access to. Plan gating renders as a
  // lock badge next to the label + an upsell screen when the section is opened.
  const visibleSections = navSections.filter(
    (s) =>
      (!s.platformAdminOnly || isPlatformAdmin) &&
      (!s.adminOrOwnerOnly || canAccessExport)
  );
  const groups = Array.from(new Set(visibleSections.map((s) => s.group)));
  const activeSection = visibleSections.some((s) => s.id === section) ? section : "profile";

  return (
    <div className="flex h-[calc(100svh-3rem)]">
      {/* Settings sidebar nav */}
      <nav className="w-80 shrink-0 border-r bg-muted/20 px-3 py-6 flex flex-col gap-6 overflow-y-auto">
        <div className="px-2">
          <p className="text-base font-semibold">Settings</p>
        </div>
        {groups.map((group) => (
          <div key={group} className="flex flex-col gap-0.5">
            <p className="px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
              {group}
            </p>
            {visibleSections
              .filter((s) => s.group === group)
              .map((s) => {
                const isActive = activeSection === s.id;
                const locked = s.requiresFeature ? !isFeatureEnabled(s.requiresFeature) : false;
                return (
                  <Link
                    key={s.id}
                    to="/dashboard/settings"
                    search={{ section: s.id, destinationId: undefined, provider: "" }}
                    className={cn(
                      "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
                      isActive
                        ? "bg-accent text-accent-foreground font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                  >
                    <s.icon className="size-4 shrink-0" />
                    <span className="flex-1">{s.label}</span>
                    {locked && <Lock className="size-3 text-muted-foreground/70 shrink-0" />}
                  </Link>
                );
              })}
          </div>
        ))}
      </nav>

      {/* Content area */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-4xl px-8 py-8">
          {activeSection === "clients" ? (
            <ClientsSection provider={provider} />
          ) : (
            (() => {
              const ActiveSection = sectionComponents[activeSection] ?? ProfileSection;
              return <ActiveSection />;
            })()
          )}
        </div>
      </main>
    </div>
  );
}
